import { useState, useEffect } from "react";
import { Loader2, LogOut, Plus, Trash2, Save } from "lucide-react";
import { api, formatError, uploadFile, fileUrl } from "../lib/api";
import { Input, Textarea, Field, Select } from "../components/FormBits";
import { toast } from "sonner";
import { useSettings } from "../context/SettingsContext";

const CATS = ["climatisation", "gainable", "froid-commercial", "chambre-froide"];
const CHECKLIST = ["LOGO", "TÉLÉPHONE", "EMAIL", "ADRESSE", "HORAIRES", "PHOTOS RÉELLES", "RÉALISATIONS", "MARQUES", "QUALIFICATIONS", "ASSURANCES", "AVIS GOOGLE", "MENTIONS LÉGALES", "ANALYTICS", "SEARCH CONSOLE", "FORMULAIRES"];

function Login({ onOk }) {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/login", { email, password: pw }); onOk(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-cloud px-6">
      <form onSubmit={submit} data-testid="admin-login" className="w-full max-w-sm bg-white border border-line rounded-md p-8">
        <div className="font-heading font-black leading-[0.85] mb-8"><span className="block text-xl text-ink">HP FROID</span><span className="block text-[10px] tracking-[0.25em] text-ice font-bold">ADMINISTRATION</span></div>
        <Field label="E-mail"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" /></Field>
        <div className="h-4" />
        <Field label="Mot de passe"><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="admin-password" /></Field>
        <button disabled={busy} data-testid="admin-login-btn" className="w-full mt-6 bg-ice text-white rounded-md py-3 font-bold hover:bg-icehover transition-colors flex items-center justify-center gap-2">
          {busy && <Loader2 className="animate-spin" size={18} />} Se connecter
        </button>
      </form>
    </div>
  );
}

function SettingsTab() {
  const { settings, refresh } = useSettings();
  const [s, setS] = useState(settings);
  useEffect(() => setS(settings), [settings]);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setCo = (k, v) => setS((p) => ({ ...p, company: { ...(p.company || {}), [k]: v } }));
  const setPr = (grp, k, v) => setS((p) => ({ ...p, pricing: { ...(p.pricing || {}), [grp]: { ...((p.pricing || {})[grp] || {}), [k]: v } } }));
  const save = async () => { try { await api.put("/settings", s); await refresh(); toast.success("Paramètres enregistrés."); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };
  const upLogo = async (e) => { const file = e.target.files?.[0]; if (!file) return; const r = await uploadFile(file); set("logo_url", r.url); toast.success("Logo importé."); };
  const upHero = async (e) => { const file = e.target.files?.[0]; if (!file) return; const r = await uploadFile(file); set("hero_url", r.url); toast.success("Image Hero importée."); };

  return (
    <div className="space-y-8 max-w-3xl">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Téléphone"><Input value={s.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Field>
        <Field label="E-mail"><Input value={s.email || ""} onChange={(e) => set("email", e.target.value)} /></Field>
      </div>
      <Field label="Logo" hint="Importer un logo remplacera le logo typographique par défaut.">
        <div className="flex items-center gap-4">
          {s.logo_url && <img src={fileUrl(s.logo_url)} alt="logo" className="h-10" />}
          <input type="file" accept=".png,.jpg,.jpeg,.webp" onChange={upLogo} />
        </div>
      </Field>
      <Field label="Image Hero (page d’accueil)" hint="Image recommandée : vraie réalisation HP Froid représentative de la climatisation ou du froid.">
        <div className="flex items-center gap-4">
          {s.hero_url && <img src={fileUrl(s.hero_url)} alt="hero" className="h-14 w-24 object-cover rounded" />}
          <input type="file" accept=".png,.jpg,.jpeg,.webp" onChange={upHero} />
        </div>
      </Field>
      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2 font-semibold text-ink"><input type="checkbox" className="w-4 h-4 accent-[#0284C7]" checked={!!s.reviews_enabled} onChange={(e) => set("reviews_enabled", e.target.checked)} /> Avis clients à connecter (activer la section)</label>
        <label className="flex items-center gap-2 font-semibold text-ink"><input type="checkbox" className="w-4 h-4 accent-[#0284C7]" checked={s.brands_enabled !== false} onChange={(e) => set("brands_enabled", e.target.checked)} /> Afficher les marques installées</label>
      </div>
      <div>
        <h3 className="font-heading font-bold text-lg text-ink mb-4">Entreprise (masqué tant que vide)</h3>
        <div className="grid sm:grid-cols-2 gap-5">
          {[["founded", "Date de création"], ["team", "Équipe"], ["qualifications", "Qualifications"], ["capacity", "Attestation capacité"], ["insurance", "Assurances"], ["partners", "Partenaires"]].map(([k, l]) => (
            <Field key={k} label={l}><Input value={(s.company || {})[k] || ""} onChange={(e) => setCo(k, e.target.value)} /></Field>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-heading font-bold text-lg text-ink mb-1">Tarifs entretien ponctuel (€ HT)</h3>
        <p className="text-xs text-muted2 mb-4">Page Entretien. Laisser vide pour conserver le tarif actuellement affiché.</p>
        <div className="grid sm:grid-cols-3 gap-5">
          {[["mono", "Mono-split"], ["bi", "Bi-split"], ["multi3", "Multi-split 3 unités"], ["multi4", "Multi-split 4 unités"], ["multi5", "Multi-split 5 unités"]].map(([k, l]) => (
            <Field key={k} label={l}><Input type="number" min="0" value={((s.pricing || {}).ponctuel || {})[k] ?? ""} onChange={(e) => setPr("ponctuel", k, e.target.value)} /></Field>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-heading font-bold text-lg text-ink mb-1">Contrat Essentiel — prix mensuels (€ HT / mois)</h3>
        <p className="text-xs text-muted2 mb-4">Tant qu’aucun prix n’est renseigné, la page affiche « Sur devis ».</p>
        <div className="grid sm:grid-cols-3 gap-5">
          {[["mono", "Mono-split"], ["bi", "Bi-split"], ["u3", "3 unités"], ["u4", "4 unités"], ["u5", "5 unités"]].map(([k, l]) => (
            <Field key={k} label={l}><Input type="number" min="0" value={((s.pricing || {}).essentiel || {})[k] ?? ""} onChange={(e) => setPr("essentiel", k, e.target.value)} /></Field>
          ))}
        </div>
        <Field label="Contenu inclus dans Essentiel (une ligne par élément)" hint="Laisser vide pour utiliser le contenu standard.">
          <Textarea rows={5} value={s.essentiel_includes || ""} onChange={(e) => set("essentiel_includes", e.target.value)} />
        </Field>
      </div>
      <div>
        <h3 className="font-heading font-bold text-lg text-ink mb-1">Contrat Sérénité — prix mensuels (€ HT / mois)</h3>
        <p className="text-xs text-muted2 mb-4">Tant qu’aucun prix n’est renseigné, la page affiche « Sur devis ».</p>
        <div className="grid sm:grid-cols-3 gap-5">
          {[["mono", "Mono-split"], ["bi", "Bi-split"], ["u3", "3 unités"], ["u4", "4 unités"], ["u5", "5 unités"]].map(([k, l]) => (
            <Field key={k} label={l}><Input type="number" min="0" value={((s.pricing || {}).serenite || {})[k] ?? ""} onChange={(e) => setPr("serenite", k, e.target.value)} /></Field>
          ))}
        </div>
        <Field label="Contenu inclus dans Sérénité (une ligne par élément)" hint="Laisser vide pour utiliser le contenu standard.">
          <Textarea rows={5} value={s.serenite_includes || ""} onChange={(e) => set("serenite_includes", e.target.value)} />
        </Field>
      </div>
      <Field label="Google Analytics ID (optionnel)" hint="Laisser vide tant qu’aucun compte réel n’est connecté."><Input value={s.analytics_ga_id || ""} onChange={(e) => set("analytics_ga_id", e.target.value)} /></Field>
      <button onClick={save} data-testid="settings-save" className="inline-flex items-center gap-2 bg-ink text-white rounded-md px-7 py-3 font-bold hover:bg-inkhover"><Save size={17} /> Enregistrer</button>
    </div>
  );
}

function RealisationsTab() {
  const empty = { title: "", category: "climatisation", city: "", description: "", material: "", date: "", images: [] };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const load = () => api.get("/realisations").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const upImg = async (e) => { const file = e.target.files?.[0]; if (!file) return; const r = await uploadFile(file); set("images", [...form.images, r.url]); };
  const add = async () => { if (!form.title) return toast.error("Titre requis."); try { await api.post("/realisations", form); setForm(empty); await load(); toast.success("Réalisation ajoutée."); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };
  const del = async (id) => { await api.delete(`/realisations/${id}`); await load(); };

  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div className="bg-cloud border border-line rounded-md p-6 space-y-4 h-fit">
        <h3 className="font-heading font-bold text-lg text-ink">Ajouter une réalisation</h3>
        <Field label="Titre"><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="real-title" /></Field>
        <Field label="Catégorie"><Select options={CATS} value={form.category} onChange={(e) => set("category", e.target.value)} /></Field>
        <Field label="Ville"><Input value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
        <Field label="Description"><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
        <Field label="Matériel utilisé"><Input value={form.material} onChange={(e) => set("material", e.target.value)} /></Field>
        <Field label="Date (optionnel)"><Input value={form.date} onChange={(e) => set("date", e.target.value)} /></Field>
        <Field label="Photos"><input type="file" accept=".png,.jpg,.jpeg,.webp" onChange={upImg} /></Field>
        {form.images.length > 0 && <div className="flex gap-2 flex-wrap">{form.images.map((im, i) => <img key={i} src={fileUrl(im)} className="h-16 w-16 object-cover rounded" alt="" />)}</div>}
        <button onClick={add} data-testid="real-add" className="inline-flex items-center gap-2 bg-ice text-white rounded-md px-6 py-3 font-bold hover:bg-icehover"><Plus size={17} /> Ajouter</button>
      </div>
      <div className="space-y-3">
        <h3 className="font-heading font-bold text-lg text-ink">Réalisations ({items.length})</h3>
        {items.length === 0 && <p className="text-muted2 text-sm">Aucune réalisation. La page publique affiche des exemples tant que vide.</p>}
        {items.map((r) => (
          <div key={r.id} className="flex items-center gap-4 bg-white border border-line rounded-md p-3">
            <img src={fileUrl(r.images?.[0])} className="h-14 w-14 object-cover rounded bg-cloud" alt="" />
            <div className="flex-1 min-w-0"><p className="font-semibold text-ink truncate">{r.title}</p><p className="text-xs text-muted2">{r.category} · {r.city}</p></div>
            <button onClick={() => del(r.id)} className="text-muted2 hover:text-red-500 p-2"><Trash2 size={17} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SubmissionsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/submissions").then(({ data }) => setItems(data)); }, []);
  return (
    <div className="space-y-3">
      {items.length === 0 && <p className="text-muted2">Aucune demande pour le moment.</p>}
      {items.map((s) => (
        <div key={s.id} className="bg-white border border-line rounded-md p-5">
          <div className="flex justify-between"><span className="font-bold text-ink uppercase text-sm">{s.type}</span><span className="text-xs text-muted2">{new Date(s.created_at).toLocaleString("fr-FR")}</span></div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-3 text-sm">
            {Object.entries(s.data).filter(([, v]) => v).map(([k, v]) => <p key={k}><span className="text-muted2">{k}:</span> <span className="text-ink">{String(v)}</span></p>)}
          </div>
          {s.files?.length > 0 && <div className="flex gap-2 mt-3">{s.files.map((f, i) => <a key={i} href={fileUrl(f)} target="_blank" rel="noreferrer" className="text-ice text-sm underline">Pièce {i + 1}</a>)}</div>}
        </div>
      ))}
    </div>
  );
}

function ChecklistTab() {
  return (
    <div className="max-w-xl">
      <p className="text-maintext mb-6">Éléments à fournir / valider avant la mise en ligne :</p>
      <ul className="grid sm:grid-cols-2 gap-2">
        {CHECKLIST.map((c) => <li key={c} className="bg-white border border-line rounded-md px-4 py-3 font-semibold text-ink text-sm">☐ {c}</li>)}
      </ul>
    </div>
  );
}

export default function Admin() {
  const [auth, setAuth] = useState(null);
  const [tab, setTab] = useState("settings");
  useEffect(() => { document.title = "Administration — HP Froid"; api.get("/auth/me").then(() => setAuth(true)).catch(() => setAuth(false)); }, []);
  const logout = async () => { await api.post("/auth/logout"); setAuth(false); };

  if (auth === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-ice" size={32} /></div>;
  if (!auth) return <Login onOk={() => setAuth(true)} />;

  const tabs = [["settings", "Paramètres"], ["realisations", "Réalisations"], ["submissions", "Demandes"], ["checklist", "Checklist"]];
  return (
    <div className="min-h-screen bg-cloud">
      <header className="bg-white border-b border-line sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-heading font-black text-ink">HP FROID · Admin</span>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm font-bold text-maintext hover:text-ink"><LogOut size={16} /> Déconnexion</button>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-2 mb-8 flex-wrap">
          {tabs.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`px-5 py-2.5 rounded-md font-semibold text-sm border ${tab === k ? "bg-ink text-white border-ink" : "bg-white text-maintext border-line"}`}>{l}</button>
          ))}
        </div>
        {tab === "settings" && <SettingsTab />}
        {tab === "realisations" && <RealisationsTab />}
        {tab === "submissions" && <SubmissionsTab />}
        {tab === "checklist" && <ChecklistTab />}
      </div>
    </div>
  );
}
